import React, {useEffect, useState} from 'react';
import {useNavigate, useParams} from "react-router-dom";
import {
    adminAddProductThesaurus,
    adminDeleteProductThesaurus,
    adminGetProductThesaurus,
    adminUpdateProductThesaurus,
    getProducts
} from '../../../services/api/admin';
import PaginatedTable from "../../Block/Table/PaginatedTable";
import {Button} from "flowbite-react";
import {Edit, TrashBin} from "flowbite-react-icons/outline";
import LoadSpinner from "../../Common/LoadSpinner";
import Modal from "react-modal";
import ListPagination from "../../Block/Pagination/ListPagination";
import ConfirmationModal from "../../Modal/ConfirmationModal";
import ModalAddEditThesaurus from "../../Modal/ModalAddEditThesaurus";
import DropdownSelect from "../../Formik/DropdownSelect";
import {useFormik} from "formik";

const ProductThesaurusList = () => {
    const headers = [
        {data: 'Product', classes: 'sl-border-right sl-font-bold'},
        {data: 'Thesaurus', classes: 'sl-font-bold'},
    ];

    const [productThesaurusData, setProductThesaurusData] = useState([]);
    const [productThesaurusFetched, setProductThesaurusFetched] = useState(false);
    const [allProducts, setAllProducts] = useState([]);
    const [addEditError, setAddEditError] = useState('');

    const formik = useFormik({
        initialValues: { product: null },
        onSubmit: () => { onClickFilter(); }
    });

    const [deleteRequestOpen, setDeleteRequestOpen] = useState(false);
    const [editRequestOpen, setEditRequestOpen] = useState(false);
    const [addRequestOpen, setAddRequestOpen] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState(null);

    const navigate = useNavigate();
    const {per_page} = useParams();
    const [page, setPage] = useState(1);
    const [pagination, setPagination] = useState({
        total: 0,
        count: 0,
        per_page: parseInt(per_page, 10) || 10,
        current_page: parseInt(page, 10) || 1,
        total_pages: 1
    });

    const [activeThesaurusFilterOptions, setActiveThesaurusFilterOptions] = useState({
        page: pagination.current_page,
        per_page: pagination.per_page,
        product: null,
    });

    useEffect(() => {
        const fetchProducts = async () => {
            const products = await getProducts();
            if (products) {
                setAllProducts(products);
            }
        };

        fetchProducts();
    }, []);

    const fetchProductThesaurus = async () => {
        if (!activeThesaurusFilterOptions?.page || !parseInt(activeThesaurusFilterOptions?.per_page) > 0) return;
        if (!productThesaurusData.length && !productThesaurusFetched)
            try {
                const {data, meta} = await adminGetProductThesaurus(activeThesaurusFilterOptions);

                const transformedData = data.map((item) => {
                    const tempData = {};

                    if (item.product_name !== undefined) {
                        tempData.product_name = {
                            data: item.product_name,
                            classes: "sl-min-w-10"
                        };
                    }

                    if (item.name !== undefined) {
                        tempData.name = {
                            data: item.name,
                            classes: "sl-min-w-10"
                        };
                    }

                    tempData.edit = {
                        data: <Edit size={20} color={"#344054"} className={"sl-m-auto"} />,
                        onClick: () => handleToggleEdit(item),
                        classes: "sl-action-edit sl-cursor-pointer"
                    };

                    tempData.delete = {
                        data: <TrashBin size={20} color={"#344054"} className={"sl-m-auto"} />,
                        onClick: () => handleToggleDelete(item),
                        classes: "sl-action-delete sl-border-right sl-cursor-pointer"
                    };

                    return tempData;
                });

                setProductThesaurusFetched(true);
                setProductThesaurusData(transformedData);
                setPagination(meta.pagination);
                setActiveThesaurusFilterOptions({
                    ...activeThesaurusFilterOptions,
                    page: meta.pagination.current_page,
                    per_page: meta.pagination.per_page
                })

            } catch (e) {
                console.error('Error fetching product thesaurus:', e);
            }
    }

    const handleToggleAdd = () => {
        setAddRequestOpen(!addRequestOpen);
        setAddEditError('');
    };

    const handleAdd = async ({productId, thesaurus}) => {
        try {
            const data = await adminAddProductThesaurus(productId, thesaurus);
            if (data.data.message !== 'Successful') {
                setAddEditError(data.data.message);
            } else {
                handleToggleAdd();
                onClickReset();
                setAddEditError('')
            }
        } catch (e) {
            console.error('Error adding thesaurus:', e);
        }
    }

    const handleToggleEdit = (item) => {
        setEditRequestOpen(!editRequestOpen);
        setSelectedRequest(item);
        setAddEditError('');
    };

    const handleEdit = async ({id, thesaurus}) => {
        if (thesaurus !== selectedRequest.name) {
            try {
                const data = await adminUpdateProductThesaurus(id, thesaurus);
                if (data.data.message !== 'Successful') {
                    setAddEditError(data.data.message);
                } else {
                    handleToggleEdit();
                    onClickReset();
                    setAddEditError('')
                }
            } catch (e) {
                console.error('Error editing thesaurus:', e);
            }
        }
    }

    const handleToggleDelete = (item) => {
        setDeleteRequestOpen(!deleteRequestOpen);
        setSelectedRequest(item);
    };

    const handleDelete = async (item) => {
        try {
            await adminDeleteProductThesaurus(item.id);
            setDeleteRequestOpen(false);
            onClickReset();
        } catch (e) {
            console.error('Error deleting thesaurus:', e);
        }
    }

    React.useEffect(() => {
            fetchProductThesaurus(activeThesaurusFilterOptions).then(r => console.log('Product thesaurus fetched'));
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [activeThesaurusFilterOptions]);

    React.useEffect(() => {
            if (!page && !per_page) {
                onClickReset(true);
                return
            }

            setActiveThesaurusFilterOptions({
                ...activeThesaurusFilterOptions,
                page: page || 1,
                per_page: per_page || 10
            })
            setProductThesaurusData([]);
            setProductThesaurusFetched(false);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [page, per_page]);

    const onClickFilter = () => {
        setActiveThesaurusFilterOptions({
            ...activeThesaurusFilterOptions,
            product: formik.values.product ? formik.values.product.id : null,
            page: 1,
            per_page: 10
        });
        setProductThesaurusData([]);
        setProductThesaurusFetched(false);
        navigate(`/admin/thesaurus`);
    }

    const onClickReset = (skipNavigate = false) => {
        setActiveThesaurusFilterOptions({
            page: 1,
            per_page: 10,
            product: null,
        })
        setPage(1);
        setProductThesaurusData([]);
        setProductThesaurusFetched(false);
        formik.setFieldValue('product', null);
        if (!skipNavigate) navigate(`/admin/thesaurus`);
    }

    return (
        <div>
            <main className="sl-main sl-mt-8">
                <div className="sl-container sl-m-auto sl-my-8 sl-flex sl-flex-col sl-gap-3">
                    <div className={"sl-flex sl-flex-row sl-justify-space-between"}>
                        <div className={"sl-flex sl-flex-row sl-gap-2 sl-relative"}>
                            <div>
                                <DropdownSelect
                                    formik={formik}
                                    name={'product'}
                                    selectedItem={formik.values.product}
                                    fieldDefaultText={'Select product'}
                                    options={allProducts}
                                    onChange={(selected) => formik.setFieldValue('product', selected)}
                                    maxLabelLength={30}
                                    elementClasses={'sl-min-w-86 sl-max-w-86 sl-text-left sl-overflow-y-hidden sl-absolute sl-z-10'}
                                >
                                </DropdownSelect>
                            </div>

                            <div className={"sl-flex sl-gap-2 sl-absolute sl-left-300 "}>
                                <Button color="blue" onClick={onClickFilter}>Filter</Button>
                                <Button color="gray" onClick={onClickReset}>Reset</Button>
                            </div>
                        </div>

                        <Button color="blue" onClick={handleToggleAdd}>Add new</Button>
                    </div>

                    {deleteRequestOpen && <ConfirmationModal
                        title={`Delete thesaurus`}
                        message={'Are you sure you want to delete thesaurus "' + selectedRequest.name +
                                    '" for product ' + selectedRequest.product_name + '?'}
                        btnConfirm={'Delete'}
                        handleConfirm={async () => handleDelete(selectedRequest)}
                        handleClose={() => {
                            setDeleteRequestOpen(false);
                            setSelectedRequest(null);
                        }}
                    />}

                    {selectedRequest && <Modal
                        isOpen={editRequestOpen}
                        onRequestClose={handleToggleEdit}
                        className="sl-modal-content sl-max-h-185"
                        overlayClassName="sl-modal sl-block sl-rounded-md sl-bg-gray-900-opacity-50">
                        <ModalAddEditThesaurus
                            item={selectedRequest}
                            handleAction={handleEdit}
                            onHandleToggle={handleToggleEdit}
                            title={"Edit thesaurus"}
                            errorMessage={addEditError}
                            message={'Update the thesaurus term for product ' + selectedRequest.product_name}
                        />
                    </Modal>}

                    {addRequestOpen && <Modal
                        isOpen={addRequestOpen}
                        onRequestClose={handleToggleAdd}
                        className="sl-modal-content sl-max-h-185"
                        overlayClassName="sl-modal sl-block sl-rounded-md sl-bg-gray-900-opacity-50">
                        <ModalAddEditThesaurus
                            products={allProducts}
                            handleAction={handleAdd}
                            onHandleToggle={handleToggleAdd}
                            title={"Add new thesaurus"}
                            errorMessage={addEditError}
                            message={'Provide the following details to add a new thesaurus term:'}
                        />
                    </Modal>}

                    {productThesaurusFetched ? (
                        <div className="sl-table-container">
                            <PaginatedTable headers={headers} data={productThesaurusData}/>
                        </div>
                    ) : (
                        <div className="sl-self-center sl-mt-3 sl-mb-21">
                            <LoadSpinner/>
                        </div>
                    )}
                    {pagination.total_pages > 1 && productThesaurusFetched && (
                        <ListPagination
                            paginationData={pagination}
                            onPageChange={(newPage) => setPage(newPage)}
                        />
                    )}
                </div>
            </main>
        </div>
    );
};

export default ProductThesaurusList;
